
import { type FeedbackStatus } from "@/types";
import FeedbackItem from "./FeedbackItem";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

type Feedback = {
  id: string;
  title: string;
  description: string;
  status: FeedbackStatus;
  votes: number;
  hasVoted: boolean;
  board_id?: string;
  created_at?: string;
  // Scoring fields
  score_method?: 'value_effort' | 'rice';
  value_score?: number;
  effort_score?: number;
  reach_score?: number;
  impact_score?: number;
  confidence_score?: number;
  effort_rice_score?: number;
};

type FeedbackListProps = {
  feedback: Feedback[];
  companyStatuses?: any[];
  onVote: (id: string) => void;
  isAdmin?: boolean;
  onStatusChange?: (id: string, status: FeedbackStatus) => void;
  onDelete?: (id: string) => void;
  onSaveScores?: (
    id: string, 
    data: {
      score_method: 'value_effort' | 'rice',
      value_score?: number,
      effort_score?: number,
      reach_score?: number,
      impact_score?: number,
      confidence_score?: number,
      effort_rice_score?: number,
    }
  ) => void;
};

const FeedbackList = ({ 
  feedback, 
  companyStatuses,
  onVote, 
  isAdmin = false,
  onStatusChange,
  onDelete,
  onSaveScores
}: FeedbackListProps) => {
  const [boardNames, setBoardNames] = useState<Record<string, string>>({});
  
  // Fetch board names for feedback items
  useEffect(() => {
    const fetchBoardNames = async () => {
      if (feedback.length === 0) return;
      
      // Get unique board IDs
      const boardIds = [...new Set(feedback.map(item => item.board_id).filter(Boolean))];
      
      if (boardIds.length === 0) return;
      
      const { data } = await supabase
        .from("feedback_boards")
        .select("id, name")
        .in("id", boardIds);
      
      if (data) {
        const boardMap: Record<string, string> = {};
        data.forEach(board => {
          boardMap[board.id] = board.name;
        });
        setBoardNames(boardMap);
      }
    };
    
    fetchBoardNames();
  }, [feedback]);

  return (
    <div className="space-y-2">
      {feedback.map((item) => (
        <FeedbackItem
          key={item.id}
          {...item}
          companyStatuses={companyStatuses}
          onVote={onVote}
          isAdmin={isAdmin}
          onStatusChange={onStatusChange}
          onDelete={onDelete}
          boardName={item.board_id ? boardNames[item.board_id] : undefined}
          // Pass scoring props
          scoreMethod={item.score_method as 'value_effort' | 'rice'}
          value_score={item.value_score}
          effort_score={item.effort_score}
          reach_score={item.reach_score}
          impact_score={item.impact_score}
          confidence_score={item.confidence_score}
          effort_rice_score={item.effort_rice_score}
          onSaveScores={onSaveScores}
        />
      ))}
    </div>
  );
};

export default FeedbackList;
